import { supabase } from '@/lib/supabase';
import type { ProductItem } from '@/services/productsService';
import { normalizeFindSubcategory } from '@/lib/findSubcategories';

export interface SaleEntry {
  id: number;
  product_id: number;
  position: number;
  /** When true, pin this sale item to the top of the sale grid. */
  visible_on_top: boolean;
  created_at: string;
}

export interface SaleEntryWithProduct extends SaleEntry {
  product: ProductItem | null;
}

const TABLE = 'back_2_school_sale';

/**
 * Get all sale entries ordered by position/id (same order the public
 * site uses), enriched with the matching product from the products table.
 * Entries whose product no longer exists are kept but marked `product: null`
 * so admins can see and remove broken rows.
 */
export const getSaleEntries = async (): Promise<SaleEntryWithProduct[]> => {
  const { data: entries, error } = await supabase
    .from(TABLE)
    .select('*')
    .order('position', { ascending: true })
    .order('id', { ascending: true });

  if (error) throw error;

  const rows = (entries || []) as SaleEntry[];
  if (rows.length === 0) return [];

  const ids = rows.map((r) => r.product_id);
  const { data: products, error: prodError } = await supabase
    .from('products')
    .select('*')
    .in('_id', ids);

  if (prodError) throw prodError;

  const productMap = new Map<number, ProductItem>();
  ((products || []) as unknown as ProductItem[]).forEach((p) => {
    productMap.set(Number((p as any)._id), p);
  });

  return rows.map((row) => ({
    ...row,
    product: productMap.get(row.product_id) || null,
  }));
};

/**
 * Add a product to the sale at the end of the list.
 * Throws if the product is already in the sale (unique product_id).
 */
export const addSaleEntry = async (productId: number): Promise<SaleEntry> => {
  // Next position = current max + 1
  const { data: topRows, error: maxError } = await supabase
    .from(TABLE)
    .select('position')
    .order('position', { ascending: false })
    .limit(1);

  if (maxError) throw maxError;

  const nextPosition =
    topRows && topRows.length > 0 ? Number(topRows[0]?.position ?? 0) + 1 : 1;

  const { data, error } = await supabase
    .from(TABLE)
    .insert({ product_id: productId, position: nextPosition })
    .select()
    .single();

  if (error) {
    // 23505 = unique_violation on product_id
    if (error.code === '23505') {
      throw new Error('This product is already in the Back 2 School sale');
    }
    throw new Error(error.message || 'Failed to add product to sale');
  }
  return data as SaleEntry;
};

export const removeSaleEntry = async (id: number): Promise<void> => {
  const { error } = await supabase.from(TABLE).delete().eq('id', id);
  if (error) throw new Error(error.message || 'Failed to remove product from sale');
};

/** Toggle the "visible on top" (pinned) flag for a single sale entry. */
export const setSaleEntryVisibleOnTop = async (
  id: number,
  visible: boolean,
): Promise<void> => {
  const { error } = await supabase
    .from(TABLE)
    .update({ visible_on_top: visible })
    .eq('id', id);

  if (error) throw new Error(error.message || 'Failed to update visibility');
};

export interface SaleBulkUpdates {
  /** Pin / unpin the selected sale entries. */
  visibleOnTop?: boolean;
  /** Set the Finds subcategory on the selected products. */
  findSubcategory?: string;
}

/**
 * Small bulk edit for the Back 2 School sale.
 *
 * - `visibleOnTop` updates `back_2_school_sale.visible_on_top` for the rows
 *   whose product IDs are passed in.
 * - `findSubcategory` updates `products.find_subcategory` for the same
 *   products (validated / normalised to None | Budget | Quality).
 *
 * Only the fields provided are written; everything else stays untouched.
 * Returns the number of sale rows updated (for the visibility change).
 */
export const updateSaleEntriesBulk = async (
  productIds: number[],
  updates: SaleBulkUpdates,
): Promise<number> => {
  const cleanIds = Array.from(
    new Set(productIds.filter((id) => Number.isSafeInteger(id) && id > 0)),
  );
  if (cleanIds.length === 0) return 0;

  let updatedSaleRows = 0;

  if (updates.visibleOnTop !== undefined) {
    const { data, error } = await supabase
      .from(TABLE)
      .update({ visible_on_top: updates.visibleOnTop })
      .in('product_id', cleanIds)
      .select('id');

    if (error) throw new Error(error.message || 'Failed to update visibility');
    updatedSaleRows = (data || []).length;
  }

  if (updates.findSubcategory !== undefined) {
    const { error } = await supabase
      .from('products')
      .update({
        find_subcategory: normalizeFindSubcategory(updates.findSubcategory),
        updated_at: new Date().toISOString(),
      })
      .in('_id', cleanIds);

    if (error) throw new Error(error.message || 'Failed to update subcategory');
  }

  return updatedSaleRows;
};

/**
 * Persist a new display order for the sale.
 *
 * `orderedIds` is the full list of sale-entry `id`s in the desired top-to-
 * bottom order. Positions are reassigned as 1..N and written in parallel.
 */
export const reorderSaleEntries = async (orderedIds: number[]): Promise<void> => {
  const cleanIds = orderedIds.filter((id) => Number.isSafeInteger(id) && id > 0);
  if (cleanIds.length === 0) return;

  const updates = cleanIds.map((id, index) =>
    supabase
      .from(TABLE)
      .update({ position: index + 1 })
      .eq('id', id),
  );

  const results = await Promise.all(updates);
  const failed = results.find((r) => r.error);
  if (failed?.error) {
    throw new Error(failed.error.message || 'Failed to reorder');
  }
};

/**
 * Swap the position of an entry with its neighbor in the ordered list.
 */
export const moveSaleEntry = async (
  entryId: number,
  direction: 'up' | 'down'
): Promise<void> => {
  const { data: all, error } = await supabase
    .from(TABLE)
    .select('*')
    .order('position', { ascending: true })
    .order('id', { ascending: true });

  if (error) throw error;

  const rows = (all || []) as SaleEntry[];
  const idx = rows.findIndex((r) => r.id === entryId);
  if (idx === -1) return;

  const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
  if (swapIdx < 0 || swapIdx >= rows.length) return; // already first/last

  const a = rows[idx];
  const b = rows[swapIdx];

  const { error: errA } = await supabase
    .from(TABLE)
    .update({ position: b.position })
    .eq('id', a.id);
  if (errA) throw new Error(errA.message || 'Failed to reorder');

  const { error: errB } = await supabase
    .from(TABLE)
    .update({ position: a.position })
    .eq('id', b.id);
  if (errB) throw new Error(errB.message || 'Failed to reorder');
};
